import React, { useState } from "react";
import {
  Container,
  Navbar,
  Button,
  Offcanvas,
  Nav,
  Tabs,
  Tab,
} from "react-bootstrap";
import {
  List,          // hamburger
  HouseDoor,     // Home
  PersonCircle,  // Profile
  Tags,          // Pricing
  Envelope,      // Contact
} from "react-bootstrap-icons";

/**
 * TabsWithLeftOffcanvas_v04
 * - Uses react-bootstrap Tabs/Tab with icon titles
 * - Left Offcanvas nav with icons
 * - Top header includes a hamburger (List icon) to open Offcanvas
 *
 * Setup:
 *   npm i react-bootstrap bootstrap react-bootstrap-icons
 *   // In your app entry:
 *   import "bootstrap/dist/css/bootstrap.min.css";
 */

type TabKey = "home" | "profile" | "pricing" | "contact";

interface Props {
  appTitle?: string;
  defaultTab?: TabKey;
}

export const TabsWithLeftOffcanvas_v04: React.FC<Props> = ({
  appTitle = "React Tabs Demo",
  defaultTab = "profile",
}) => {
  const [activeKey, setActiveKey] = useState<TabKey>(defaultTab);
  const [showNav, setShowNav] = useState(false);
  const offcanvasId = "leftNavOffcanvas";

  const handleSelect = (k: string | null) => {
    if (!k) return;
    setActiveKey(k as TabKey);
  };

  const handleNavClick = (k: TabKey) => {
    setActiveKey(k);
    setShowNav(false);
  };

  return (
    <div className="min-vh-100 d-flex flex-column">
      {/* Header */}
      <Navbar bg="light" className="shadow-sm sticky-top">
        <Container fluid className="px-3 d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-secondary"
              onClick={() => setShowNav(true)}
              aria-label="Open navigation"
              aria-controls={offcanvasId}
            >
              <List size={20} />
            </Button>
            <Navbar.Brand className="fw-semibold m-0">{appTitle}</Navbar.Brand>
          </div>
        </Container>
      </Navbar>

      {/* Left Offcanvas Nav */}
      <Offcanvas
        id={offcanvasId}
        show={showNav}
        onHide={() => setShowNav(false)}
        placement="start"
        backdrop
        scroll
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="fw-semibold">Navigation</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Nav activeKey={activeKey} className="flex-column" onSelect={handleSelect}>
            <Nav.Link eventKey="home" onClick={() => handleNavClick("home")}>
              <HouseDoor className="me-2" /> Home
            </Nav.Link>
            <Nav.Link eventKey="profile" onClick={() => handleNavClick("profile")}>
              <PersonCircle className="me-2" /> Profile
            </Nav.Link>
            <Nav.Link eventKey="pricing" onClick={() => handleNavClick("pricing")}>
              <Tags className="me-2" /> Pricing
            </Nav.Link>
            <Nav.Link
              eventKey="contact"
              onClick={() => handleNavClick("contact")}
              disabled
              aria-disabled
              tabIndex={-1}
            >
              <Envelope className="me-2" /> Contact (disabled)
            </Nav.Link>
          </Nav>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Main Content */}
      <Container fluid className="flex-grow-1 py-4 px-3 px-md-4">
        <Tabs
          id="icon-tabs"
          className="mb-3"
          activeKey={activeKey}
          onSelect={handleSelect}
          mountOnEnter
          unmountOnExit
        >
          <Tab
            eventKey="home"
            title={
              <span className="d-inline-flex align-items-center">
                <HouseDoor className="me-2" /> Home
              </span>
            }
          >
            <div className="p-2">Tab content for <strong>Home</strong>.</div>
          </Tab>

          <Tab
            eventKey="profile"
            title={
              <span className="d-inline-flex align-items-center">
                <PersonCircle className="me-2" /> Profile
              </span>
            }
          >
            <div className="p-2">Tab content for <strong>Profile</strong>.</div>
          </Tab>

          <Tab
            eventKey="pricing"
            title={
              <span className="d-inline-flex align-items-center">
                <Tags className="me-2" /> Pricing
              </span>
            }
          >
            <div className="p-2">
              <div className="mb-2"><strong>Starter:</strong> $0 — basic features</div>
              <div className="mb-2"><strong>Pro:</strong> $19/mo — advanced tools</div>
              <div className="mb-0"><strong>Enterprise:</strong> Contact sales</div>
            </div>
          </Tab>

          <Tab
            eventKey="contact"
            title={
              <span className="d-inline-flex align-items-center">
                <Envelope className="me-2" /> Contact
              </span>
            }
            disabled
          >
            <div className="p-2">Tab content for Contact (disabled).</div>
          </Tab>
        </Tabs>
      </Container>

      {/* Footer */}
      <footer className="border-top py-3">
        <Container fluid className="px-3">
          <div className="text-center text-secondary small">
            © {new Date().getFullYear()} {appTitle}. All rights reserved.
          </div>
        </Container>
      </footer>
    </div>
  );
};

export default TabsWithLeftOffcanvas_v04;
